import { type TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { compareDesc } from 'date-fns';
import type { CSSProperties } from 'react';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Switcher } from '@sorare/core/src/atoms/navigation/Switcher';
import { WithScrollButtons } from '@sorare/core/src/atoms/navigation/WithScrollButtons';
import { BodyM, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMainColor } from '@sorare/core/src/hooks/useMainColor';
import { isLightColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import {
  desktopAndAbove,
  laptopAndAbove,
  mediumDesktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { PromoBanners_PromotionalEvent } from './__generated__/index.graphql';

const StyledFullViewportScroll = styled(FullViewportScroll)`
  background:
    linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 0,
      rgba(var(--c-rgb-black), 0) 70%,
      var(--c-black) 100%
    ),
    var(--background-url, var(--c-black)) top center;
  background-size: cover;
  container-type: inline-size;
`;

const Container = styled(Vertical)`
  width: 100%;
  padding: calc(var(--unit) * 6) 0 var(--double-unit);
  --card-width: 250px;
  flex-direction: column-reverse;
  align-items: center;
  min-height: 400px;
  @container ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
    min-height: 400px;
    --card-width: 340px;
    padding: var(--quadruple-unit) 0;
  }
  @container ${laptopAndAbove} {
    --card-width: 480px;
  }
  @container ${desktopAndAbove} {
    --card-width: 540px;
    min-height: 420px;
  }

  @container ${mediumDesktopAndAbove} {
    --card-width: 720px;
    justify-content: space-around;
  }
`;

const SkeletonContainer = styled(Horizontal)`
  width: calc(100% + var(--container-padding) * 2);
  margin: 0 calc(-1 * var(--container-padding));
`;
const PromoBannersSkeleton = styled(Skeleton)`
  width: 100%;
  height: 300px;

  @container ${tabletAndAbove} {
    height: 360px;
  }
  @container ${desktopAndAbove} {
    height: 420px;
  }
`;

const LeftSide = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  isolation: isolate;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  @container ${tabletAndAbove} {
    max-width: calc(100% - var(--card-width));
    width: 450px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--double-unit);
`;

const Cards = styled(Vertical)`
  --side-image: url('');
  background: var(--side-image) no-repeat center center;
  background-size: contain;
  flex-shrink: 0;
  height: 180px;
  width: 100%;
  @container ${tabletAndAbove} {
    height: 100%;
  }
`;

const Logo = styled.img`
  width: auto;
  height: calc(7 * var(--unit));
`;

type Props = {
  promotionalEvents: PromoBanners_PromotionalEvent[];
  sport: Sport;
  loading: boolean;
};

export const PromoBanners = ({ promotionalEvents, sport, loading }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { promotionalEvents: configPromotionalEvents } = useConfigContext();

  const configPromotionalEventsWithBanner = useMemo(() => {
    return configPromotionalEvents
      .filter(p => p.pictureUrl)
      .sort((a, b) => compareDesc(a.startDate, b.startDate));
  }, [configPromotionalEvents]);

  const promotionalEventsWithBanner = useMemo(
    () =>
      promotionalEvents
        .filter(p => p.pictureUrl)
        .sort((a, b) => compareDesc(a.startDate, b.startDate)),
    [promotionalEvents]
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedPromotionalEvent =
    promotionalEventsWithBanner[activeIndex] || {};
  const mainColor = useMainColor(
    selectedPromotionalEvent?.pictureUrl ?? undefined
  );

  const tab =
    ('hasInstantBuy' in selectedPromotionalEvent &&
      selectedPromotionalEvent.hasInstantBuy) ||
    isType(selectedPromotionalEvent, 'CartPurchaseRewardPromotionalEvent')
      ? generateSportPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, { sport })
      : generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, { sport });

  if (loading && configPromotionalEventsWithBanner.length > 0) {
    return (
      <StyledFullViewportScroll>
        <SkeletonContainer>
          <PromoBannersSkeleton />
        </SkeletonContainer>
      </StyledFullViewportScroll>
    );
  }
  if (promotionalEventsWithBanner.length === 0) {
    return null;
  }

  return (
    <WithScrollButtons
      wrapperRef={wrapperRef}
      scrollContainerRef={scrollContainerRef}
    >
      <StyledFullViewportScroll
        ref={scrollContainerRef}
        style={
          {
            '--background-url': selectedPromotionalEvent?.backgroundPictureUrl
              ? `url(${selectedPromotionalEvent.backgroundPictureUrl})`
              : undefined,
          } as CSSProperties
        }
      >
        <Container>
          <LeftSide>
            <Chip color={isLightColor(mainColor) ? 'black' : 'yellow'}>
              <Horizontal>
                <FontAwesomeIcon icon={faClock} />
                <TimeLeft time={selectedPromotionalEvent.endDate} />
              </Horizontal>
            </Chip>

            <Vertical gap={2}>
              {selectedPromotionalEvent.logoUrl ? (
                <Logo src={selectedPromotionalEvent.logoUrl} />
              ) : (
                <BrandXXL>{selectedPromotionalEvent.title}</BrandXXL>
              )}
              <BodyM color="var(--c-nd-600)">
                {selectedPromotionalEvent.description}
              </BodyM>
              <Actions>
                <Button
                  size="medium"
                  color="primary"
                  to={generatePath(
                    `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${selectedPromotionalEvent.slug}#market`
                  )}
                >
                  <FormattedMessage
                    id="PromoBanners.cta"
                    defaultMessage="Shop now"
                  />
                </Button>
                {!!selectedPromotionalEvent.blogUrl && (
                  <Button
                    size="medium"
                    color="secondary"
                    to={selectedPromotionalEvent.blogUrl}
                  >
                    <FormattedMessage {...glossary.learnMore} />
                  </Button>
                )}
              </Actions>
            </Vertical>
            <Switcher
              numberOfItems={promotionalEventsWithBanner.length}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
            />
          </LeftSide>

          <Cards
            style={
              {
                '--side-image': `url(${selectedPromotionalEvent.bannerAssetsPictureUrl})`,
              } as CSSProperties
            }
          />
        </Container>
      </StyledFullViewportScroll>
    </WithScrollButtons>
  );
};

PromoBanners.fragments = {
  promotionalEvent: gql`
    fragment PromoBanners_PromotionalEvent on PromotionalEventInterface {
      slug
      title
      description
      startDate
      endDate
      blogUrl
      logoUrl
      pictureUrl
      backgroundPictureUrl
      bannerAssetsPictureUrl
    }
  ` as TypedDocumentNode<PromoBanners_PromotionalEvent>,
};
